import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  CellLeftV8,
  order2Sorting,
  TableCellV8,
  FormCol3,
  EditCellV8,
  CellCenterV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  CellDateTimeV8,
  CellDateV8,
  EyeColumnV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  isActiveColumn,
  lastDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
  dateColumn,
  dateExpiryColumn,
} from 'shared-pages/common/grid-helpfunc';
import { FieldMultiMaterialDlg } from 'shared-pages/components';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { isAfisCyprus, isRebattery } from 'shared-pages/common/constants';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    canEdit,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateCollector] = useMutation(gqlUpdate);
  const [addNewCollector] = useMutation(gqlAdd);
  const [deleteCollector] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Collector'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'name',
        header: t('Επωνυμία Συλλέκτη'),
        columnName: t('Επωνυμία Συλλέκτη'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'addressLine1',
        header: t('Διεύθυνση'),
        columnName: t('Διεύθυνση'),
        accessorKey: 'addressLine1',
        orderByField: 'addressLine1',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'phone',
        header: t('Τηλέφωνο'),
        columnName: t('Τηλέφωνο'),
        accessorKey: 'phone',
        orderByField: 'phone',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'companyVat',
        header: t('Α.Φ.Μ.'),
        columnName: t('Α.Φ.Μ.'),
        accessorKey: 'companyVat',
        orderByField: 'companyVat',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      ...(!isAfisCyprus
        ? [
            {
              id: 'companyTaxOffice',
              header: t('Δ.Ο.Υ.'),
              columnName: t('Δ.Ο.Υ.'),
              accessorKey: 'companyTaxOffice',
              orderByField: 'companyTaxOffice',
              cell: TableCellV8,
              size: 50,
              meta: { type: 'text' },
            },
          ]
        : []),
      ...(!isRebattery
        ? [
            {
              id: 'contractNo',
              header: t('Αριθμός Σύμβασης'),
              columnName: t('Αριθμός Σύμβασης'),
              accessorKey: 'contractNo',
              orderByField: 'contractNo',
              cell: TableCellV8,
              size: 50,
              meta: { type: 'text' },
            },
          ]
        : []),

      {
        id: 'contractExpDate',
        header: t('Λήξη Σύμβασης'),
        columnName: t('Λήξη Σύμβασης'),
        accessorKey: 'contractExpDate',
        orderByField: 'contractExpDate',
        ...dateExpiryColumn(t),
      },
      {
        id: 'insurancePolicyNumber',
        header: t('Αριθμός Ασφαλιστηρίου Αστικής Ευθύνης'),
        columnName: t('Αριθμός Ασφαλιστηρίου Αστικής Ευθύνης'),
        accessorKey: 'insurancePolicyNumber',
        orderByField: 'insurancePolicyNumber',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'insuranceExpirationDate',
        header: t('Λήξη Ασφαλιστηρίου Αστικής Ευθύνης'),
        columnName: t('Λήξη Ασφαλιστηρίου Αστικής Ευθύνης'),
        accessorKey: 'insuranceExpirationDate',
        orderByField: 'insuranceExpirationDate',
        ...dateExpiryColumn(t),
      },
      {
        id: 'hazardousWastePermit',
        header: isRebattery
          ? t('Αρ. Πρωτ. / ΑΔΑ Άδειας Συλλογής Μπαταριών')
          : t('Άδεια Μεταφοράς Επικίνδυνων'),
        columnName: isRebattery
          ? t('Αρ. Πρωτ. / ΑΔΑ Άδειας Συλλογής Μπαταριών')
          : t('Άδεια Μεταφοράς Επικίνδυνων'),
        accessorKey: 'hazardousWastePermit',
        orderByField: 'hazardousWastePermit',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'hazardousWasteExpDate',
        header: isRebattery
          ? t('Λήξη Άδειας Συλλογής Μεταφοράς Μπαταριών')
          : t('Άδεια Επικινδύνων'),
        columnName: isRebattery
          ? t('Λήξη Άδειας Συλλογής Μεταφοράς Μπαταριών')
          : t('Άδεια Επικινδύνων'),
        accessorKey: 'hazardousWasteExpDate',
        orderByField: 'hazardousWasteExpDate',
        ...dateExpiryColumn(t),
      },
      {
        id: 'adrConsultantAgrExpDate',
        header: t('Λήξη Συμφωνητικού με ΣΑΜΕΕ'),
        columnName: t('Λήξη Συμφωνητικού με ΣΑΜΕΕ'),
        accessorKey: 'adrConsultantAgrExpDate',
        orderByField: 'adrConsultantAgrExpDate',
        ...dateExpiryColumn(t),
      },
      isActiveColumn(t, i18n),
      ...(canEdit
        ? [
            {
              id: 'edit',
              header: t('Ενέργειες'),
              columnName: t('Ενέργειες'),
              accessorKey: 'edit',
              orderByField: 'lastUser',
              size: 120,
              cell: ({ row, table }) => {
                return canEdit ? EditCellV8({ row, table }) : <>-</>;
              },
            },
          ]
        : []),
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="collectors-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
            };
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Collectors - OnSaveRow row', row, changes);
            const values = { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewCollector({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addCollectortype.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateCollector({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε τον Συλλέκτη <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteCollector({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε ο Συλλέκτης');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
